import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { isApiError } from '@shared/api';
import { clearQueryResponseCache } from '@shared/lib/query-helpers';
import { helpdeskEditQuery, patchHelpdeskTask } from './task-edit';

const memberSchema = z.object({ id: z.uuid(), displayName: z.string().nullable() });
const commandSchema = z.object({ members: z.array(memberSchema), etag: z.string() });
const operationSchema = z.object({
  key: z.uuid(),
  profileId: z.uuid().optional(),
  command: commandSchema.nullable(),
});
type AssignmentCommand = z.infer<typeof commandSchema>;
type AssignmentOperation = z.infer<typeof operationSchema>;

function isOperation(value: unknown): value is AssignmentOperation {
  return operationSchema.safeParse(value).success;
}

export function isAssignmentConflict(error: unknown): boolean {
  return isApiError(error) && [409, 412, 428].includes(error.status);
}

/** Only explicit domain/client rejection proves that an attempted PATCH was not accepted. */
function isUncertain(error: unknown): boolean {
  return !(isApiError(error) && error.status >= 400 && error.status < 500 && error.status !== 408);
}

/** One access-scoped command survives overlay/row unmounts, including an uncertain response. */
export function useHelpdeskAssignment(viewerAccessScope: string, taskUuid: string) {
  const client = useQueryClient();
  const mutationKey = ['helpdesk-task-edit', viewerAccessScope, taskUuid];
  const filters = { mutationKey, exact: true };
  const operations = useMutationState({
    filters,
    select: (mutation) => ({
      status: mutation.state.status,
      error: mutation.state.error,
      variables: isOperation(mutation.state.variables) ? mutation.state.variables : undefined,
    }),
  });
  const latest = operations.at(-1);
  const ownsOperation = Boolean(latest?.variables);
  const uncertain = latest?.status === 'error' && isUncertain(latest.error);
  const pending = latest?.status === 'pending';
  const mutation = useMutation({
    mutationKey,
    // Keep an unresolved command until the authenticated QueryClient is cleared. Prune older
    // settled attempts below so there is only one retained operation per edited task.
    gcTime: Infinity,
    retry: false,
    mutationFn: async (operation: AssignmentOperation) => {
      if (!operation.command) {
        const current = await client.fetchQuery(helpdeskEditQuery(viewerAccessScope, taskUuid));
        const profileId = z.uuid().parse(operation.profileId);
        // Freeze the full set and validator BEFORE dispatch; a retry never re-reads/rebases them.
        operation.command = {
          members: current.data.assignees.some((person) => person.id === profileId)
            ? current.data.assignees
            : [...current.data.assignees, { id: profileId, displayName: null }],
          etag: current.etag,
        };
      }
      return patchHelpdeskTask(taskUuid, {
        body: { assigneeIds: operation.command.members.map((person) => person.id) },
        etag: operation.command.etag,
        idempotencyKey: operation.key,
      });
    },
    onSuccess: async () => {
      clearQueryResponseCache();
      await client.invalidateQueries({ queryKey: ['helpdesk', viewerAccessScope] });
    },
    onError: async (error) => {
      if (!isAssignmentConflict(error)) return;
      clearQueryResponseCache();
      // Refetch failure stays on the edit query, where the picker exposes recovery and blocks edits.
      await client.refetchQueries({
        queryKey: helpdeskEditQuery(viewerAccessScope, taskUuid).queryKey,
        type: 'all',
      });
    },
  });
  const submit = (operation: AssignmentOperation, retry = false) => {
    const previous = client.getMutationCache().findAll(filters).at(-1);
    if (previous?.state.status === 'pending') return;
    if (
      retry &&
      (!isOperation(previous?.state.variables) ||
        previous.state.variables.key !== operation.key ||
        previous.state.status !== 'error' ||
        !isUncertain(previous.state.error))
    )
      return;
    if (!retry && previous?.state.status === 'error' && isUncertain(previous.state.error)) return;
    // The new mutation takes over the same retry identity; remove old settled cache entries.
    for (const entry of client.getMutationCache().findAll(filters)) {
      if (entry.state.status !== 'pending') client.getMutationCache().remove(entry);
    }
    mutation.mutate(operation);
  };
  return {
    pending: ownsOperation && pending,
    uncertain: ownsOperation && uncertain,
    blockedByOther: !ownsOperation && (pending || uncertain),
    error: ownsOperation && latest?.status === 'error' ? latest.error : null,
    save: (command: AssignmentCommand) => {
      submit({ key: crypto.randomUUID(), command });
    },
    take: (profileId: string) => {
      submit({ key: crypto.randomUUID(), profileId, command: null });
    },
    retry: () => {
      if (uncertain && latest.variables) submit(latest.variables, true);
    },
    resetRejected: () => {
      for (const entry of client.getMutationCache().findAll(filters)) {
        if (
          entry.state.status === 'error' &&
          !isUncertain(entry.state.error) &&
          isOperation(entry.state.variables)
        ) {
          client.getMutationCache().remove(entry);
        }
      }
    },
  };
}
